import React, { useState, useEffect } from "react";
import Icon from "@mdi/react";
import { mdiAlertCircle, mdiCashRefund } from "@mdi/js";
import { DateTime } from "luxon";
import BaseModal from "./BaseModal";
import CustomFormInput from "./forms/CustomFormInput";
import { formatCurrencyFromMinor } from "../utils/currency";
import "./CancelTravelPeriodModal.css";

/**
 * CancelTravelPeriodModal Component
 *
 * Used in three contexts:
 *   1. Delete flow — cancel bookings then delete the travel period (default copy)
 *   2. Conflict flow — cancel conflicting home-schedule bookings only; period is NOT deleted
 *   3. Currency unlock — cancel a travel period's bookings so its currency can change
 *      Pass `title`, `confirmLabel`, `processingLabel`, and `warningText` to override copy.
 *
 * Paid bookings are never cancelled silently: each needs a Refund or Credit decision
 * (same step as SickDayConfirmModal; Credit is not offered to guests). Confirm stays
 * disabled until every paid booking has one. The caller issues the returns (a full return
 * cancels the booking), then cancels the unpaid ids.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onConfirm - (message, unpaidBookingIds, paidDecisions) => Promise
 *   where paidDecisions is [{ bookingId, action: 'refund'|'credit' }]
 * @param {Array} props.affectedBookings - each { id, bookingDate, startTime, studentName,
 *   isPaid, isGuest, refundableAmount (cents), currency }
 * @param {boolean} [props.isProcessing=false]
 * @param {string} [props.title] - Modal title (default: "Cancel Travel Period")
 * @param {string} [props.confirmLabel] - Confirm button label (default: "Cancel Bookings & Delete Period")
 * @param {string} [props.processingLabel] - Label shown while processing (default: "Deleting...")
 * @param {string} [props.warningText] - Bold warning line (default: delete-period copy)
 * @param {Function} [props.onKeep] - date-clash flow only: keep the bookings and carry on
 *   (they stay booked; the trip's availability is set around them)
 * @param {string} [props.keepLabel] - label for the keep button
 */
const CancelTravelPeriodModal = ({
    isOpen,
    onClose,
    onConfirm,
    affectedBookings = [],
    isProcessing = false,
    title = "Cancel Travel Period",
    confirmLabel = "Cancel Bookings & Delete Period",
    processingLabel = "Deleting...",
    warningText = null,
    onKeep = null,
    keepLabel = "Keep These Bookings",
}) => {
    const [cancellationMessage, setCancellationMessage] = useState(
        "I need to cancel your booking as I have had to change my travel plans. I apologise for the inconvenience. Please rebook using my latest availability."
    );

    // Per-paid-booking decision: bookingId -> 'refund' | 'credit' (undefined = undecided)
    const [paidDecisions, setPaidDecisions] = useState({});

    const unpaidBookings = affectedBookings.filter(b => !b.isPaid);
    const paidBookings = affectedBookings.filter(b => b.isPaid);

    // A new list (modal reopened, or re-fetched after a stale-list 409) starts undecided.
    useEffect(() => {
        if (isOpen) setPaidDecisions({});
    }, [isOpen, affectedBookings]);

    const allPaidDecided = paidBookings.every(b => paidDecisions[b.id]);

    const defaultWarning = `This will cancel all ${affectedBookings.length} booking${affectedBookings.length !== 1 ? 's' : ''} and permanently delete this travel period.`;

    const handleConfirm = async () => {
        if (isProcessing || !allPaidDecided) return;
        const decisions = paidBookings.map(b => ({ bookingId: b.id, action: paidDecisions[b.id] }));
        await onConfirm(cancellationMessage, unpaidBookings.map(b => b.id), decisions);
    };

    const setPaidDecision = (bookingId, action) => {
        setPaidDecisions(prev => ({ ...prev, [bookingId]: action }));
    };

    const handleClose = () => {
        if (!isProcessing) onClose();
    };

    const formatBookingDate = (isoDate) => {
        try {
            return DateTime.fromISO(isoDate).toFormat('dd MMM');
        } catch {
            return isoDate;
        }
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={handleClose}
            title={title}
            icon={mdiAlertCircle}
            iconColor="var(--dl-color-danger)"
            maxWidth={520}
            footer={
                <>
                    <button className="base-modal-cancel-btn" onClick={handleClose} disabled={isProcessing}>
                        Go Back
                    </button>
                    {onKeep && (
                        <button className="base-modal-secondary-btn" onClick={onKeep} disabled={isProcessing}>
                            {keepLabel}
                        </button>
                    )}
                    <button
                        className="base-modal-save-btn cancel-travel-confirm-btn"
                        onClick={handleConfirm}
                        disabled={isProcessing || !allPaidDecided}>
                        {isProcessing ? processingLabel : confirmLabel}
                    </button>
                </>
            }>
            <div className="cancel-travel-body">
                <div className="cancel-travel-warning">
                    <Icon path={mdiAlertCircle} size={1.2} color="var(--dl-color-danger)" />
                    <p>
                        <strong>{warningText || defaultWarning}</strong>
                        <br />
                        {onKeep ? 'If you cancel, students are notified via in-app message.' : 'Students will be notified via in-app message.'}
                    </p>
                </div>

                {unpaidBookings.length > 0 && (
                    <div className="cancel-travel-bookings">
                        <h3>{onKeep ? 'Bookings in these dates:' : 'Bookings that will be cancelled:'}</h3>
                        <ul className="bookings-list">
                            {unpaidBookings.map((booking, index) => (
                                <li key={booking.id || index} className="booking-item">
                                    <div className="booking-details">
                                        <span className="booking-time">
                                            {formatBookingDate(booking.bookingDate)} at {booking.startTime}
                                        </span>
                                        <span className="booking-student">
                                            {booking.studentName || booking.studentEmail}
                                        </span>
                                        {booking.status && (
                                            <span className="booking-status-badge">{booking.status}</span>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Paid bookings — require a per-booking decision (mirrors SickDayConfirmModal) */}
                {paidBookings.length > 0 && (
                    <div className="cancel-travel-paid">
                        <div className="cancel-travel-paid-header">
                            <Icon path={mdiCashRefund} size={0.8} color="var(--dl-color-sick-red)" />
                            <h3>Paid bookings: choose what to do ({paidBookings.length})</h3>
                        </div>
                        <p className="form-hint">
                            These students have paid, so choose refund or credit. Both cancel the lesson and return
                            their money. Your message tells them to book another time with you.
                        </p>

                        <ul className="cancel-travel-paid-list">
                            {paidBookings.map(booking => (
                                <li key={booking.id} className="cancel-travel-paid-item">
                                    <div className="cancel-travel-paid-info">
                                        <span className="booking-time">
                                            {formatBookingDate(booking.bookingDate)} at {booking.startTime}
                                        </span>
                                        <span className="booking-student">
                                            {booking.studentName || booking.studentEmail}
                                        </span>
                                        {booking.refundableAmount > 0 && (
                                            <span className="cancel-travel-paid-amount">
                                                {formatCurrencyFromMinor(booking.refundableAmount, booking.currency)}{" "}
                                                paid
                                            </span>
                                        )}
                                    </div>
                                    <div className="cancel-travel-paid-options">
                                        <CustomFormInput
                                            type="radio"
                                            variant="turquoise"
                                            layout="standard"
                                            name={`travel-paid-${booking.id}`}
                                            checked={paidDecisions[booking.id] === "refund"}
                                            onChange={() => setPaidDecision(booking.id, "refund")}
                                            disabled={isProcessing}
                                            label="Refund"
                                        />
                                        {/* Credit is an account-holder privilege — not available to guests */}
                                        {!booking.isGuest && (
                                            <CustomFormInput
                                                type="radio"
                                                variant="turquoise"
                                                layout="standard"
                                                name={`travel-paid-${booking.id}`}
                                                checked={paidDecisions[booking.id] === "credit"}
                                                onChange={() => setPaidDecision(booking.id, "credit")}
                                                disabled={isProcessing}
                                                label="Credit"
                                            />
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {!allPaidDecided && (
                            <p className="cancel-travel-paid-warning">
                                Choose an option for each paid booking to continue.
                            </p>
                        )}
                    </div>
                )}

                <div className="cancel-travel-message-section">
                    <label htmlFor="cancel-travel-message" className="cancel-travel-message-label">
                        Cancellation Message
                    </label>
                    <p className="form-hint">
                        Sent to every student whose booking is cancelled, refunded, or credited. Edit as required.
                    </p>
                    <textarea
                        id="cancel-travel-message"
                        className="cancel-travel-message-textarea"
                        value={cancellationMessage}
                        onChange={e => setCancellationMessage(e.target.value)}
                        rows={4}
                        disabled={isProcessing}
                    />
                </div>
            </div>
        </BaseModal>
    );
};

export default CancelTravelPeriodModal;
